// src/HomePage/Game/Werewolf/WerewolfContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { database } from '../../../firebaseConfig';
import {
  ref,
  onValue,
  set,
  push,
  get,
  update,
  onDisconnect
} from 'firebase/database';
import { ROLE_DEFINITIONS } from './roleEngine';

export const WerewolfContext = createContext();

const AVATAR_POOL = ['🥷', '🧙‍♂️', '🧝‍♀️', '🧛‍♂️', '🦁', '🦉', '🐻', '🦊', '🦄', '🐲', '🤖', '⚔️', '🧑‍🌾', '⚡', '👑'];

export const getAvatarIcon = (uid) => {
  if (!uid) return '👤';
  let hash = 0;
  for (let i = 0; i < uid.length; i++) {
    hash = uid.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_POOL[Math.abs(hash) % AVATAR_POOL.length];
};

export const WerewolfProvider = ({ children, user, isCatechist, studentId }) => {
  const [rooms, setRooms] = useState([]);
  
  const [currentRoomId, setCurrentRoomId] = useState(() => {
    return localStorage.getItem('ww_current_room_id') || null;
  });
  
  const [currentRoom, setCurrentRoom] = useState(null);
  const [lastFinishedGame, setLastFinishedGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 🔒 KIỂM TRA CHÍNH XÁC DANH TÍNH (KHÔNG DÙNG ID GIẢ LẬP)
  const catechistUid = localStorage.getItem('uid');
  const savedStudentId = localStorage.getItem('studentId') || studentId;
  const isGlvLoggedIn = localStorage.getItem('isLoggedIn') === 'true';

  const activeUid = isCatechist
    ? catechistUid
    : savedStudentId;

  // Xác nhận người dùng đã đăng nhập GLV hoặc đã có ID Thiếu Nhi
  const isAuthenticated = Boolean(
    activeUid && (
      (isCatechist && isGlvLoggedIn) ||
      (!isCatechist && savedStudentId)
    )
  );

  const activeName = user?.fullName || localStorage.getItem('studentFullName') || localStorage.getItem('catechistFullName') || '';
  const activeHolyName = user?.holyName || localStorage.getItem('studentHolyName') || localStorage.getItem('catechistHolyName') || '';

  useEffect(() => {
    const roomsRef = ref(database, 'werewolf_rooms');

    const unsubscribe = onValue(roomsRef, (snapshot) => {
      const data = snapshot.val();
      const savedRoomId = localStorage.getItem('ww_current_room_id') || currentRoomId;

      if (!data) {
        setRooms([]);
        setCurrentRoomId(null);
        setCurrentRoom(null);
        localStorage.removeItem('ww_current_room_id');
        setLoading(false);
        return;
      }

      const roomList = Object.entries(data).map(([key, value]) => ({
        roomId: key,
        ...value
      }));

      const now = Date.now();
      const validRooms = roomList.filter(r => r.status !== 'CANCELLED' && (now - (r.createdAt || 0) < 12 * 3600 * 1000));

      setRooms(validRooms);

      if (savedRoomId && data[savedRoomId] && activeUid) {
        const targetRoom = data[savedRoomId];
        const players = targetRoom.players || {};

        if (players[activeUid]) {
          setCurrentRoomId(savedRoomId);
          setCurrentRoom({ roomId: savedRoomId, ...targetRoom });
          
          if (targetRoom.gameState?.phase === 'ENDED') {
            setLastFinishedGame(targetRoom.gameState);
          }

          const onlineRef = ref(database, `werewolf_rooms/${savedRoomId}/players/${activeUid}/isOnline`);
          set(onlineRef, true);
        } else {
          setCurrentRoomId(null);
          setCurrentRoom(null);
          localStorage.removeItem('ww_current_room_id');
        }
      } else {
        setCurrentRoomId(null);
        setCurrentRoom(null);
        localStorage.removeItem('ww_current_room_id');
      }

      setLoading(false);
    }, (err) => {
      console.error("Lỗi kết nối RTDB Werewolf:", err);
      setError("Không thể kết nối máy chủ Game.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [activeUid]);

  const createRoom = async ({ roomName, maxPlayers, password }) => {
    if (!isAuthenticated || !activeUid) {
      return { success: false, message: 'Vui lòng đăng nhập tài khoản trước khi tạo phòng!' };
    }

    try {
      setError('');
      const roomCode = Math.floor(100000 + Math.random() * 900000).toString();
      const roomsRef = ref(database, 'werewolf_rooms');
      const newRoomRef = push(roomsRef);
      const roomId = newRoomRef.key;
      const isLocked = Boolean(password && password.trim());

      const roomData = {
        roomId,
        roomCode,
        roomName: roomName.trim() || `Phòng của ${activeHolyName} ${activeName}`,
        hostUid: activeUid,
        hostName: activeName,
        hostHolyName: activeHolyName,
        hostRole: isCatechist ? 'catechist' : 'student',
        isLocked,
        password: isLocked ? password.trim() : '',
        maxPlayers: Number(maxPlayers) || 10,
        currentPlayersCount: 1,
        status: 'WAITING',
        createdAt: Date.now(),
        gameRules: {
          dayDuration: 0,
          nightDuration: 0,
          voteDuration: 0,
          revealRoleOnDeath: true
        },
        rolesConfig: {
          werewolfCount: 2,
          seer: true,
          bodyguard: true,
          witch: true,
          hunter: false,
          cupid: false
        },
        players: {
          [activeUid]: {
            uid: activeUid,
            fullName: activeName,
            holyName: activeHolyName,
            avatar: getAvatarIcon(activeUid),
            isHost: true,
            isReady: true,
            isCatechist,
            seatIndex: 1,
            isAlive: true,
            isOnline: true,
            joinedAt: Date.now()
          }
        }
      };

      await set(newRoomRef, roomData);

      const playerOnlineRef = ref(database, `werewolf_rooms/${roomId}/players/${activeUid}/isOnline`);
      onDisconnect(playerOnlineRef).set(false);

      localStorage.setItem('ww_current_room_id', roomId);
      setCurrentRoomId(roomId);
      setCurrentRoom(roomData);
      return { success: true, roomId, roomCode };
    } catch (err) {
      console.error("Lỗi tạo phòng:", err);
      setError("Tạo phòng thất bại: " + err.message);
      return { success: false, message: err.message };
    }
  };

  const joinRoom = async (targetRoomId, passwordInput = '') => {
    if (!isAuthenticated || !activeUid) {
      return { success: false, message: 'Vui lòng đăng nhập tài khoản trước khi vào phòng!' };
    }

    try {
      setError('');
      const roomRef = ref(database, `werewolf_rooms/${targetRoomId}`);
      const snapshot = await get(roomRef);

      if (!snapshot.exists()) {
        return { success: false, message: 'Phòng không tồn tại hoặc đã bị xóa.' };
      }

      const roomData = snapshot.val();
      const players = roomData.players || {};

      if (roomData.status === 'PLAYING' && !players[activeUid]) {
        return { success: false, message: 'Phòng đang trong ván game!' };
      }

      if (roomData.isLocked && roomData.password !== passwordInput.trim()) {
        return { success: false, message: 'Mật khẩu phòng không đúng!' };
      }

      if (!players[activeUid] && Object.keys(players).length >= roomData.maxPlayers) {
        return { success: false, message: 'Phòng đã đầy người chơi!' };
      }

      const takenSeats = new Set(Object.values(players).map(p => p.seatIndex));
      let freeSeat = 1;
      for (let i = 1; i <= roomData.maxPlayers; i++) {
        if (!takenSeats.has(i)) {
          freeSeat = i;
          break;
        }
      }

      const updates = {};
      updates[`players/${activeUid}`] = {
        uid: activeUid,
        fullName: activeName,
        holyName: activeHolyName,
        avatar: getAvatarIcon(activeUid),
        isHost: roomData.hostUid === activeUid,
        isReady: players[activeUid]?.isReady || roomData.hostUid === activeUid,
        isCatechist,
        seatIndex: players[activeUid]?.seatIndex || freeSeat,
        isAlive: players[activeUid]?.isAlive ?? true,
        role: players[activeUid]?.role || null,
        abilityState: players[activeUid]?.abilityState || null,
        isOnline: true,
        joinedAt: players[activeUid]?.joinedAt || Date.now()
      };
      updates[`currentPlayersCount`] = Object.keys({ ...players, [activeUid]: true }).length;

      await update(roomRef, updates);

      const playerOnlineRef = ref(database, `werewolf_rooms/${targetRoomId}/players/${activeUid}/isOnline`);
      onDisconnect(playerOnlineRef).set(false);

      localStorage.setItem('ww_current_room_id', targetRoomId);
      setCurrentRoomId(targetRoomId);
      setCurrentRoom({ roomId: targetRoomId, ...roomData });
      return { success: true };
    } catch (err) {
      console.error("Lỗi vào phòng:", err);
      setError("Vào phòng thất bại: " + err.message);
      return { success: false, message: err.message };
    }
  };

  const startGame = async () => {
    if (!isAuthenticated || !currentRoomId || currentRoom?.hostUid !== activeUid) return;
    try {
      const playersList = Object.values(currentRoom.players || {});
      if (playersList.length < 4) {
        alert("Cần tối thiểu 4 người chơi!");
        return;
      }

      const rolesConfig = currentRoom.rolesConfig || {};
      const gameRules = currentRoom.gameRules || {};
      const rolePool = [];

      for (let i = 0; i < (rolesConfig.werewolfCount || 2); i++) rolePool.push('WEREWOLF');
      if (rolesConfig.seer) rolePool.push('SEER');
      if (rolesConfig.bodyguard) rolePool.push('BODYGUARD');
      if (rolesConfig.witch) rolePool.push('WITCH');
      if (rolesConfig.hunter) rolePool.push('HUNTER');
      if (rolesConfig.cupid) rolePool.push('CUPID');

      while (rolePool.length < playersList.length) {
        rolePool.push('VILLAGER');
      }

      for (let i = rolePool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [rolePool[i], rolePool[j]] = [rolePool[j], rolePool[i]];
      }

      const updatedPlayers = {};
      playersList.forEach((player, idx) => {
        const role = rolePool[idx];
        const maxUses = ROLE_DEFINITIONS[role]?.maxUsesPerGame;

        updatedPlayers[player.uid] = {
          ...player,
          role,
          isAlive: true,
          isOnline: true,
          abilityState: {
            gameUsesRemaining: (maxUses === undefined || maxUses === Infinity) ? -1 : maxUses,
            witchSaveAvailable: role === 'WITCH',
            witchPoisonAvailable: role === 'WITCH',
            lastProtectedUid: null,
            cupidCompleted: false
          }
        };
      });

      const nightSecs = Number(gameRules.nightDuration) || 0;
      const turnEndTime = nightSecs > 0 ? Date.now() + nightSecs * 1000 : null;

      const initialGameState = {
        phase: 'NIGHT_TURN_1',
        dayNumber: 1,
        turnEndTime,
        nightActions: {
          bittenUid: null,
          protectedUid: null,
          seerCheckedUid: null,
          witchSaved: false,
          witchPoisonedUid: null,
          cupidCouple: null
        },
        votes: {},
        gameLogs: [
          {
            id: Date.now(),
            day: 1,
            phase: 'BẮT ĐẦU',
            content: `Ván chơi chính thức bắt đầu với ${playersList.length} người chơi.`
          }
        ]
      };

      const roomRef = ref(database, `werewolf_rooms/${currentRoomId}`);
      await update(roomRef, {
        status: 'PLAYING',
        players: updatedPlayers,
        gameState: initialGameState
      });

      return { success: true };
    } catch (err) {
      console.error("Lỗi khởi tạo game:", err);
      return { success: false, message: err.message };
    }
  };

  const resetRoomToLobby = async () => {
    if (!currentRoomId || !currentRoom) return;
    try {
      const roomRef = ref(database, `werewolf_rooms/${currentRoomId}`);
      const players = currentRoom.players || {};
      const resetPlayers = {};

      Object.values(players).forEach(p => {
        resetPlayers[p.uid] = {
          ...p,
          isReady: p.isHost,
          isAlive: true
        };
      });

      await update(roomRef, {
        status: 'WAITING',
        players: resetPlayers
      });
    } catch (err) {
      console.error("Lỗi đưa phòng về trạng thái chờ:", err);
    }
  };

  const updateGameState = async (updates, newLog = null) => {
    if (!currentRoomId) return;
    try {
      const roomRef = ref(database, `werewolf_rooms/${currentRoomId}`);
      const currentLogs = currentRoom?.gameState?.gameLogs || [];

      const payload = {};
      Object.entries(updates).forEach(([key, val]) => {
        if (key.startsWith('players') || key.startsWith('status')) {
          payload[key] = val;
        } else if (key.startsWith('gameState/')) {
          payload[key] = val;
        } else {
          payload[`gameState/${key}`] = val;
        }
      });

      if (newLog) {
        payload['gameState/gameLogs'] = [...currentLogs, { id: Date.now(), ...newLog }];
      }

      await update(roomRef, payload);

      if (updates.phase === 'ENDED' || updates['gameState/phase'] === 'ENDED') {
        const finalGameState = {
          ...(currentRoom?.gameState || {}),
          ...updates
        };
        
        setLastFinishedGame(finalGameState);

        const historyId = Date.now().toString();
        const playersData = updates.players || currentRoom?.players || {};
        const participantUids = Object.keys(playersData);

        const finishedHistoryRecord = {
          historyId,
          roomId: currentRoomId,
          roomName: currentRoom?.roomName || 'Phòng Ma Sói',
          endedAt: Date.now(),
          winnerTeam: updates.winnerTeam || finalGameState.winnerTeam,
          winnerMsg: updates.winnerMsg || finalGameState.winnerMsg,
          players: playersData,
          gameLogs: payload['gameState/gameLogs'] || currentLogs,
          nightActions: finalGameState.nightActions || {},
          dayNumber: finalGameState.dayNumber || 1,
          participantUids
        };

        const historyRef = ref(database, `werewolf_history/${historyId}`);
        await set(historyRef, finishedHistoryRecord);

        const userHistoryUpdates = {};
        participantUids.forEach(uid => {
          userHistoryUpdates[`werewolf_user_history/${uid}/${historyId}`] = finishedHistoryRecord;
        });
        await update(ref(database), userHistoryUpdates);
      }
    } catch (err) {
      console.error("Lỗi cập nhật Game State:", err);
    }
  };

  const fetchMyGameHistory = async () => {
    if (!activeUid) return [];
    try {
      const userHistoryRef = ref(database, `werewolf_user_history/${activeUid}`);
      const snapshot = await get(userHistoryRef);
      if (!snapshot.exists()) return [];
      const data = snapshot.val();
      return Object.values(data).sort((a, b) => (b.endedAt || 0) - (a.endedAt || 0));
    } catch (err) {
      console.error("Lỗi tải lịch sử game RTDB:", err);
      return [];
    }
  };

  const toggleReady = async () => {
    if (!currentRoomId || !currentRoom) return;
    const myPlayer = currentRoom.players?.[activeUid];
    if (!myPlayer || myPlayer.isHost) return;
    const playerRef = ref(database, `werewolf_rooms/${currentRoomId}/players/${activeUid}`);
    await update(playerRef, { isReady: !myPlayer.isReady });
  };

  const switchSeat = async (targetSeatIndex) => {
    if (!currentRoomId || !currentRoom) return;
    const players = currentRoom.players || {};
    if (Object.values(players).some(p => p.seatIndex === targetSeatIndex)) return;
    const playerRef = ref(database, `werewolf_rooms/${currentRoomId}/players/${activeUid}`);
    await update(playerRef, { seatIndex: targetSeatIndex });
  };

  const updateRoomSettings = async (updates) => {
    if (!currentRoomId || currentRoom?.hostUid !== activeUid) return;
    const roomRef = ref(database, `werewolf_rooms/${currentRoomId}`);
    await update(roomRef, updates);
    return { success: true };
  };

  const kickPlayer = async (targetUid) => {
    if (!currentRoomId || currentRoom?.hostUid !== activeUid || targetUid === activeUid) return;
    const playerRef = ref(database, `werewolf_rooms/${currentRoomId}/players/${targetUid}`);
    await set(playerRef, null);
    const remainingCount = Math.max(1, (currentRoom.currentPlayersCount || 1) - 1);
    await update(ref(database, `werewolf_rooms/${currentRoomId}`), { currentPlayersCount: remainingCount });
  };

  const leaveRoom = async () => {
    if (!currentRoomId) return;
    
    localStorage.removeItem('ww_current_room_id');
    
    const roomRef = ref(database, `werewolf_rooms/${currentRoomId}`);
    const snapshot = await get(roomRef);

    if (snapshot.exists()) {
      const roomData = snapshot.val();
      const players = roomData.players || {};
      delete players[activeUid];
      const remainingUids = Object.keys(players);

      if (remainingUids.length === 0) {
        await set(roomRef, null);
      } else {
        const updates = { players, currentPlayersCount: remainingUids.length };
        if (roomData.hostUid === activeUid) {
          const newHostUid = remainingUids[0];
          updates.hostUid = newHostUid;
          updates.hostName = players[newHostUid].fullName;
          updates.hostHolyName = players[newHostUid].holyName;
          updates.hostRole = players[newHostUid].isCatechist ? 'catechist' : 'student';
          players[newHostUid].isHost = true;
          players[newHostUid].isReady = true;
        }
        await update(roomRef, updates);
      }
    }
    setCurrentRoomId(null);
    setCurrentRoom(null);
  };

  return (
    <WerewolfContext.Provider
      value={{
        rooms,
        currentRoom,
        currentRoomId,
        lastFinishedGame,
        loading,
        error,
        activeUid,
        activeName,
        activeHolyName,
        isCatechist,
        isAuthenticated, // 👈 Xuất thêm biến xác thực
        createRoom,
        joinRoom,
        leaveRoom,
        toggleReady,
        switchSeat,
        updateRoomSettings,
        kickPlayer,
        startGame,
        resetRoomToLobby,
        updateGameState,
        fetchMyGameHistory,
        setError
      }}
    >
      {children}
    </WerewolfContext.Provider>
  );
};

export const useWerewolf = () => useContext(WerewolfContext);